import { useMemo, useState } from 'react'
import { DownloadIcon, PrinterIcon } from 'lucide-react'
import { toast } from 'sonner'
import { useClubParticipationReport } from '@/api/hooks'
import { SelectShowNotice } from '@/components/current-show'
import { useCurrentShow } from '@/context/current-show'
import { PageHeading } from '@/components/page-heading'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { fetchWithActorHeaders } from '@/lib/apiClient'

import {
  getDefaultDirection,
  nextClubSort,
  sortClubRows,
  type ClubReportSort,
  type ClubReportSortKey,
} from './club-participation-sort'
import { ClubSortHeader } from './ClubSortHeader'

function buildCsvUrl(showId: string, includeUnknown: boolean): string {
  const defaultBase = typeof window !== 'undefined' ? window.location.origin : ''
  const baseUrl = (
    import.meta.env.VITE_API_BASE_URL ||
    defaultBase ||
    'http://localhost:3000'
  ).replace(/\/$/, '')
  const url = new URL(`/api/shows/${showId}/reports/club-participation`, baseUrl)
  url.searchParams.set('format', 'csv')
  if (!includeUnknown) {
    url.searchParams.set('includeUnknown', 'false')
  }
  return url.toString()
}

function buildPrintUrl(
  showId: string,
  includeUnknown: boolean,
  options: {
    showName?: string | null
    showLocation?: string | null
    showStartDate?: string | null
    showEndDate?: string | null
  }
) {
  const url = new URL('/print/reports/club-participation', window.location.origin)
  url.searchParams.set('showId', showId)
  if (options.showName) {
    url.searchParams.set('showName', options.showName)
  }
  if (options.showLocation) {
    url.searchParams.set('showLocation', options.showLocation)
  }
  if (options.showStartDate) {
    url.searchParams.set('showStartDate', options.showStartDate)
  }
  if (options.showEndDate) {
    url.searchParams.set('showEndDate', options.showEndDate)
  }
  if (!includeUnknown) {
    url.searchParams.set('includeUnknown', 'false')
  }
  return url
}

export default function ClubParticipationPage() {
  const { currentShow } = useCurrentShow()
  const [includeUnknown, setIncludeUnknown] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const [sortState, setSortState] = useState<ClubReportSort>({
    key: 'points',
    direction: getDefaultDirection('points'),
  })

  const reportQuery = useClubParticipationReport(currentShow?.id, {
    includeUnknown,
    enabled: currentShow?.enableClubs !== false,
  })

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), [])
  const percentFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  )

  const report = reportQuery.data
  const sortedRows = useMemo(() => {
    if (!report) return []
    return sortClubRows(report.rows, sortState.key, sortState.direction)
  }, [report, sortState])

  if (!currentShow) {
    return (
      <SelectShowNotice description="Choose a show to view club participation totals by exhibitor and entry." />
    )
  }

  if (currentShow.enableClubs === false || report?.enableClubs === false) {
    return (
      <div className="space-y-4">
        <header className="space-y-2">
          <PageHeading>Club Participation</PageHeading>
          <p className="text-muted-foreground">
            Club tracking is off for {currentShow.name}. To view this report, turn on Use clubs for
            this show in Show Setup → Participation.
          </p>
        </header>
      </div>
    )
  }

  const handleDownloadCsv = async () => {
    if (!report) {
      toast.error(
        'Club participation data is not available yet. Try again after loading completes.'
      )
      return
    }
    if (typeof window === 'undefined') {
      toast.warning('CSV download is unavailable in this environment.')
      return
    }

    const csvUrl = buildCsvUrl(report.showId, includeUnknown)
    try {
      setIsDownloading(true)
      const response = await fetchWithActorHeaders(csvUrl, { method: 'GET' })
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }
      const blob = await response.blob()
      const fileName = (() => {
        const disposition = response.headers.get('content-disposition')
        if (!disposition) return 'club-participation.csv'
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition)
        const encoded = match?.[1]
        const quoted = match?.[2]
        if (encoded) {
          try {
            return decodeURIComponent(encoded)
          } catch {
            return encoded
          }
        }
        return quoted ?? 'club-participation.csv'
      })()

      const { href, revoke } = await (async () => {
        const urlCreator = window.URL?.createObjectURL ?? URL.createObjectURL
        if (typeof urlCreator === 'function') {
          const objectUrl = urlCreator(blob)
          const revokeFn = window.URL?.revokeObjectURL ?? URL.revokeObjectURL
          return {
            href: objectUrl,
            revoke: () => {
              if (typeof revokeFn === 'function') revokeFn(objectUrl)
            },
          }
        }
        const text = await blob.text()
        return {
          href: `data:text/csv;charset=utf-8,${encodeURIComponent(text)}`,
          revoke: () => {},
        }
      })()

      const link = window.document.createElement('a')
      link.href = href
      link.download = fileName
      link.rel = 'noopener'
      window.document.body.appendChild(link)
      link.click()
      link.remove()
      revoke()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      if (message.includes('unavailable in this environment')) {
        toast.warning('CSV download is unavailable in this environment.')
      } else {
        toast.error(`Unable to download club participation CSV. ${message}`)
      }
    } finally {
      setIsDownloading(false)
    }
  }
  const handleOpenPrintView = () => {
    if (!currentShow?.id) {
      toast.error('Select a show before opening the print view.')
      return
    }

    const url = buildPrintUrl(currentShow.id, includeUnknown, {
      showName: currentShow.name,
      showLocation: currentShow.location,
      showStartDate: currentShow.startDate,
      showEndDate: currentShow.endDate,
    })

    const popup = window.open(url.toString(), '_blank', 'noopener')
    if (!popup) {
      toast.error('Allow pop-ups to open the printable report.')
    }
  }

  const handleSortChange = (key: ClubReportSortKey) =>
    setSortState(current => nextClubSort(current, key))

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <PageHeading>Club Participation</PageHeading>
            <p className="text-muted-foreground">
              Review how many exhibitors and entries each club contributed to {currentShow.name}.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenPrintView}
              className="w-full sm:w-auto"
            >
              <PrinterIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Open Print View
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={handleDownloadCsv}
              disabled={isDownloading || !report}
            >
              <DownloadIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              {isDownloading ? 'Preparing CSV…' : 'Download CSV'}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex items-center gap-2">
        <input
          id="include-unknown"
          type="checkbox"
          className="h-4 w-4 rounded border border-input"
          checked={includeUnknown}
          onChange={event => setIncludeUnknown(event.target.checked)}
        />
        <Label htmlFor="include-unknown" className="text-sm text-muted-foreground">
          Include exhibitors without a club
        </Label>
      </div>

      {reportQuery.isLoading ? (
        <p className="text-muted-foreground">Loading club participation…</p>
      ) : reportQuery.isError ? (
        <p className="text-destructive">
          {reportQuery.error?.message ?? 'Unable to load participation data.'}
        </p>
      ) : report?.rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No club participation has been recorded for this show yet.
          </CardContent>
        </Card>
      ) : report ? (
        <div className="space-y-4">
          <Card>
            <CardContent className="py-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Exhibitors</p>
                  <p className="text-2xl font-semibold">
                    {numberFormatter.format(report.totals.entrants)}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Entries</p>
                  <p className="text-2xl font-semibold">
                    {numberFormatter.format(report.totals.entries)}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Clubs Represented</p>
                  <p className="text-2xl font-semibold">
                    {numberFormatter.format(report.rows.length)}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Generated</p>
                  <p className="text-2xl font-semibold">
                    {new Intl.DateTimeFormat(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    }).format(new Date(report.generatedAt))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <ClubSortHeader
                    label="Club"
                    sortKey="name"
                    sort={sortState}
                    onSort={handleSortChange}
                  />
                  <ClubSortHeader
                    label="Exhibitors"
                    sortKey="exhibitors"
                    sort={sortState}
                    onSort={handleSortChange}
                    align="right"
                  />
                  <ClubSortHeader
                    label="Entries"
                    sortKey="entries"
                    sort={sortState}
                    onSort={handleSortChange}
                    align="right"
                  />
                  <ClubSortHeader
                    label="Total Points"
                    sortKey="points"
                    sort={sortState}
                    onSort={handleSortChange}
                    align="right"
                  />
                  <TableHead className="text-right">Share of Exhibitors</TableHead>
                  <TableHead className="text-right">Share of Entries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map(row => (
                  <TableRow key={`${row.clubId ?? 'none'}-${row.clubName}`}>
                    <TableCell className="font-medium">{row.clubName}</TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(row.exhibitorCount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(row.entryCount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(row.totalPoints)}
                    </TableCell>
                    <TableCell className="text-right">
                      {percentFormatter.format(row.percentOfEntrants)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {percentFormatter.format(row.percentOfEntries)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
    </div>
  )
}
